import React, { ComponentProps, PropsWithChildren, useMemo } from "react";
import { Image } from "react-bootstrap";
import { Link } from "react-router-dom";

import "./GameTile.scss";
import { formatPlayCount } from "../../../utils/helpers";
import GhostButton from "../GhostButton/GhostButton";
import { ROUTES } from "../../../utils/constants";
import PlayIcon from "../../icons/PlayIcon";
import ImageFallbackThumbnail from "../../../images/fallback-thumbnail.png";

interface GameTileProps extends ComponentProps<"div"> {
  name: string;
  slug?: string;
  thumbnail?: string;
  playCount?: number;
  playIconColor?: string;
  isSelected?: boolean;
  variant?: GameTileVariant;
  linkTo?: string;
}

export enum GameTileVariant {
  LINK = "link",
  SELECT = "select",
}

function GameTile({
  name,
  slug,
  thumbnail,
  playCount,
  playIconColor = "#8C8C8C",
  isSelected,
  variant = GameTileVariant.LINK,
  linkTo,
  className = "",
  ...restProps
}: GameTileProps) {
  return (
    <WrapperEl gameSlug={slug} variant={variant} linkTo={linkTo}>
      <div
        className={`game-tile ${isSelected ? "selected" : ""} ${className}`}
        {...restProps}
      >
        <Image
          src={thumbnail || ImageFallbackThumbnail}
          className="d-block thumbnail"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = ImageFallbackThumbnail;
          }}
        />
        <p className="text-start text-capitalize title">{name}</p>
        <p className="d-flex align-items-center gap-1 count">
          {playCount != null && playCount > 0 && (
            <>
              <PlayIcon
                width={14}
                height={14}
                fill={playIconColor}
                className="play-icon"
              />
              {formatPlayCount(playCount)}
            </>
          )}
        </p>{" "}
        {isSelected && (
          <GhostButton className="remove-btn">
            <span />
          </GhostButton>
        )}
      </div>
    </WrapperEl>
  );
}
export default GameTile;

const WrapperEl = ({
  children,
  gameSlug,
  variant,
  linkTo,
}: PropsWithChildren<{
  variant: GameTileVariant;
  gameSlug?: string;
  linkTo?: string;
}>) => {
  const link = useMemo(
    () => linkTo ?? `${ROUTES.GAMES.ROOT}/${gameSlug}/play`,
    [gameSlug, linkTo]
  );

  if (variant === GameTileVariant.SELECT) {
    return <GhostButton>{children}</GhostButton>;
  }

  return (
    <Link to={link} className="text-decoration-none">
      {children}
    </Link>
  );
};
